/**
 * Team-configurable drill settings: categories, default length, groups.
 *
 * All three live in the team's settings JSON (no schema change) and all three
 * are read through these helpers so the "never configured" fallback is the
 * same everywhere. The one rule that matters: changing a list here never edits
 * a drill or a plan. A group or category that has been removed from the list
 * keeps showing on anything already tagged with it, so un-listing is never
 * silent data loss — it only stops being offered for new tags.
 */
import type { TeamSettings } from '../types';
import { GROUPS } from './types';

export const DEFAULT_DRILL_MINUTES = 8;

type S = Pick<TeamSettings, 'drill_categories' | 'drill_default_minutes' | 'groups'> | null | undefined;

/** Trim, drop empties, de-duplicate case-insensitively, keep first spelling and order. */
export function cleanList(items: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const v = raw.trim();
    const k = v.toLowerCase();
    if (!v || seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

/** The groups offered for tagging. */
export function teamGroups(settings: S): string[] {
  const g = cleanList(settings?.groups ?? []);
  return g.length ? g : [...GROUPS];
}

/** True once the team has saved its own group list. */
export function hasCustomGroups(settings: S): boolean {
  return cleanList(settings?.groups ?? []).length > 0;
}

/**
 * The categories offered in the drill editor. When the team has never set a
 * list, it is the categories its drills already use, alphabetised.
 */
export function drillCategories(settings: S, usedByDrills: readonly (string | undefined | null)[] = []): string[] {
  if (settings?.drill_categories) return cleanList(settings.drill_categories);
  return cleanList(usedByDrills.map((c) => c ?? '')).sort((a, b) => a.localeCompare(b));
}

export function drillDefaultMinutes(settings: S): number {
  const m = settings?.drill_default_minutes;
  return typeof m === 'number' && Number.isFinite(m) && m >= 1 ? Math.min(180, Math.round(m)) : DEFAULT_DRILL_MINUTES;
}

/**
 * The options to show for one item: the team list, plus anything the item is
 * already tagged with that is no longer on it (appended, so it can still be
 * seen and unticked).
 */
export function withCurrent(list: readonly string[], current: readonly string[]): string[] {
  // Exact match on purpose: the chips toggle by exact string, so a stored
  // 'dl' must stay visible (and untickable) even when the list says 'DL'.
  const have = new Set(list);
  return [...list, ...current.filter((c, i) => c && !have.has(c) && current.indexOf(c) === i)];
}
